import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { UPSERT_INCOME_FORM_ID } from '../../components/UpsertIncomeModal/UpsertIncomeModal.meta';
import { IncomeData } from '../../useIncome';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { IncomeModel } from '../../api/useIncomeQuery';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { useOtherIncomeForm } from './useOtherIncomeForm';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { Toggle } from '../../../../CommonComponents/OPL/Toggle/Toggle';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';
import { AssetBucket } from '../components/AssetBucket/AssetBucket';
import { getIncomeOwnerModel } from '../../Income.meta';

export interface Props {
  clientId: string;
  income: IncomeModel | null;
  data: IncomeData;
  onSuccess: () => void;
}

export const OtherIncomeForm = ({ clientId, income, data, onSuccess }: Props) => {
  const {
    data: {
      formBag: { register, control, setValue },
      defaultShowAdvanced,
      earnerOptions,
      startYearOptions,
      endYearOptions,
      mutationBag,
      options,
      managementStyleValue,
      customBucketIndex,
    },
    actions: { onSubmit },
  } = useOtherIncomeForm({ clientId, income, data, onSuccess });

  const [showAdvanced, setShowAdvanced] = useState(defaultShowAdvanced);

  return (
    <form
      onSubmit={onSubmit}
      id={UPSERT_INCOME_FORM_ID}
      css={`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
      aria-label="Other income form"
    >
      {mutationBag.error && (
        <Alert
          variant="error"
          message={mutationBag.error.message}
          onRequestClose={mutationBag.reset}
        />
      )}

      <Input label="Name" {...register('name')} />

      <Controller
        name="earner"
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            label="Earner"
            value={earnerOptions
              .map(({ options }) => options)
              .flat()
              .find(({ value: v }) => v === value)}
            options={earnerOptions}
            onChange={option => {
              onChange(option?.value);
              if (option) {
                setValue(
                  'ownerModel',
                  getIncomeOwnerModel({ options: options.OwnerOptions, value: option.value }),
                );
              }
            }}
            isPortal
          />
        )}
      />

      <Controller
        control={control}
        name="amount"
        render={({ field: { value, onChange } }) => (
          <InputNumberMask
            value={value}
            label="Yearly Amount"
            decimalScale={2}
            allowNegative={false}
            leftIcon="far fa-dollar-sign"
            innerText="/year"
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            tooltip="Please specify the amount before deducting taxes in today's dollars."
          />
        )}
      />

      <ManagementStyle
        control={control}
        options={options}
        fieldName="orionManagementStyleId"
        onChange={(value: number | null) => {
          setValue('customBucketIndex', value);
        }}
      />

      <AssetBucket
        control={control}
        fieldName="customBucketIndex"
        managementStyleValue={managementStyleValue}
        options={options}
      />

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Box
            css={`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            `}
          >
            <Controller
              name="startAge"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Start Year"
                  value={startYearOptions.find(({ value: v }) => v === value)}
                  options={startYearOptions}
                  onChange={option => onChange(option?.value)}
                  tooltip="Enter the year when the income will start. To start this income immediately, leave this field blank."
                  isPortal
                />
              )}
            />

            <Controller
              name="endAge"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="End Year"
                  value={endYearOptions.find(({ value: v }) => v === value)}
                  options={endYearOptions}
                  onChange={option => {
                    onChange(option?.value);
                    setValue('endAtEndOfPlan', option?.value === -1);
                    setValue('endAtRetirement', option?.value === null);
                  }}
                  tooltip="Enter the year when the income will stop. If no year is entered, the income will stop at the end of the plan."
                  isPortal
                />
              )}
            />
          </Box>

          <Controller
            name="accountEarmarks"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                label="Asset Earmark"
                value={data.Options.EarmarkOptions.filter(({ value: v }) => value?.includes(v))}
                options={data.Options.EarmarkOptions}
                onChange={earmarks => onChange(earmarks.map(({ value: v }) => v))}
                isMulti
                isPortal
              />
            )}
          />

          <Controller
            control={control}
            name="inflation"
            render={({ field: { value, onChange } }) => (
              <InputNumberMask
                label="Income Growth"
                value={value}
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip='Enter the income growth rate applied to the Yearly Amount field. Leave this field blank to use the global "Income Growth Rate" found in Settings.'
              />
            )}
          />

          <Toggle
            {...register('isTaxFree')}
            size="small"
            helperText="If checked, this income will be free of federal and state taxes (e.g. Military combat pay). By default employment incomes are taxed."
          >
            Tax Free
          </Toggle>
        </>
      )}
    </form>
  );
};
